import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { inputCls } from "@/components/register/Fields";

const Box = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
    <p className="text-[11px] font-mono-epl uppercase tracking-wider text-slate-500 mb-4">{title}</p>
    {children}
  </div>
);
const GROUPS = [["U11", "U11 Mixed"], ["U13", "U13 Boys"], ["U15", "U15 Boys"], ["GIRLS", "Girls Only"]];

export const EditForm = ({ r, onSaved }) => {
  const [f, setF] = useState({ first_name: r.first_name, last_name: r.last_name, position: r.position, tshirt_size: r.tshirt_size });
  const save = () => api.patch(`/admin/registrations/${r.id}`, f).then(() => { toast.success("Player updated"); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Box title="Edit details" testid="detail-edit-card">
      <div className="space-y-3">
        <input data-testid="edit-first-name" className={inputCls} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
        <input data-testid="edit-last-name" className={inputCls} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
        <input data-testid="edit-position" className={inputCls} placeholder="Position" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} />
        <select data-testid="edit-size" className={inputCls} value={f.tshirt_size} onChange={(e) => setF({ ...f, tshirt_size: e.target.value })}>{["XS", "S", "M", "L", "XL"].map((s) => <option key={s}>{s}</option>)}</select>
        <button data-testid="edit-save" onClick={save} className="btn-primary w-full !py-3">Save changes</button>
      </div>
    </Box>
  );
};

export const GroupForm = ({ r, onSaved }) => {
  const [code, setCode] = useState(r.group_code);
  const [reason, setReason] = useState("");
  const save = () => api.post(`/admin/registrations/${r.id}/group`, { group_code: code, reason }).then(() => { toast.success("Group updated"); setReason(""); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Box title="Group allocation" testid="detail-group-card">
      <div className="space-y-3">
        <select data-testid="group-select" className={inputCls} value={code} onChange={(e) => setCode(e.target.value)}>{GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input data-testid="group-reason" className={inputCls} placeholder="Reason for change (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button data-testid="group-save" disabled={code === r.group_code || reason.length < 3} onClick={save} className="btn-primary w-full !py-3 disabled:opacity-40">Change group</button>
      </div>
      <div className="mt-4 space-y-2" data-testid="group-history">
        {r.group_history.slice().reverse().map((h, i) => <p key={i} className="text-xs text-slate-400">{h.group_code} · {h.by} · {fmtDate(h.at)}<br /><span className="text-slate-500">{h.reason}</span></p>)}
      </div>
    </Box>
  );
};

export const RefundPanel = ({ r, onDone }) => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const before = new Date() < new Date("2026-11-25T00:00:00+10:30");
  const manual = r.payment_source === "payment_link";
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/registrations/${r.id}/refund`, { percent: pending });
      toast.success(manual ? "Refund recorded and parent emailed" : "Refund issued via Stripe");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
    setPending(null);
  };
  return (
    <Box title="Refund & cancel" testid="detail-refund-card">
      <p className="text-xs text-slate-400 mb-4">Policy: 50% before 25 Nov 2026, non-refundable after. {before ? "Currently within the 50% window." : "Cutoff has passed."} Refunding cancels the registration and releases the place.</p>
      {manual && <p data-testid="refund-manual-note" className="text-xs text-orange-200 mb-4">Paid via your Stripe Payment Link: first issue the refund in your Stripe dashboard (Payments → this payment → Refund), then record it here.</p>}
      {pending ? (
        <div className="space-y-2">
          <p className="text-sm">Refund <strong>{money((r.amount_cents * pending) / 100)}</strong> and cancel?</p>
          <button data-testid="refund-confirm" disabled={busy} onClick={go} className="btn-primary w-full !py-3">{manual ? "Record refund" : "Confirm refund"}</button>
          <button data-testid="refund-cancel" onClick={() => setPending(null)} className="btn-ghost w-full !py-3">Back</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="refund-50" onClick={() => setPending(50)} className="btn-ghost !py-3 !px-3 text-sm">50% refund</button>
          <button data-testid="refund-100" onClick={() => setPending(100)} className="btn-ghost !py-3 !px-3 text-sm">Full refund</button>
        </div>
      )}
    </Box>
  );
};
